import React, { useState } from 'react';
import { Shield, User, Upload, Mail, Phone, CheckCircle, AlertCircle, ChevronRight, ChevronLeft, FileText, Home, CreditCard, UploadCloud } from 'lucide-react';

export default function InsuranceKYCPortal() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    policyNumber: '',
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    nic: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    district: '',
    country: 'Mauritius'
  });

  const [verification, setVerification] = useState({
    emailOtp: '',
    phoneOtp: '',
    emailVerified: false,
    phoneVerified: false,
    emailSent: false,
    phoneSent: false
  });

  const [documents, setDocuments] = useState({
    identityProof: null,
    addressProof: null,
    photo: null,
    nicFront: null,
    nicBack: null,
    passport: null,
    utilityBill: null,
    bankStatement: null,
    relationshipDoc: null,
    photo: null
  });

  const steps = [
    { id: 1, name: 'Basic Details', icon: <User className="w-5 h-5" /> },
    { id: 2, name: 'Verification', icon: <CheckCircle className="w-5 h-5" /> },
    { id: 3, name: 'Documents', icon: <Upload className="w-5 h-5" /> },
    { id: 4, name: 'Review', icon: <FileText className="w-5 h-5" /> }
  ];


  const [billOwner, setBillOwner] = useState(null);
  const [identityType, setIdentityType] = useState(null);
  const [addressProofType, setAddressProofType] = useState(null);


  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileUpload = (type, e) => {
    const file = e.target.files?.[0];
    if (file) setDocuments(prev => ({ ...prev, [type]: file }));
  };

  const sendOTP = (type) => {
    if (type === 'email') {
      setVerification({ ...verification, emailSent: true });
    } else {
      setVerification({ ...verification, phoneSent: true });
    }
  };

  const verifyOTP = (type) => {
    if (type === 'email') {
      setVerification({ ...verification, emailVerified: true });
    } else {
      setVerification({ ...verification, phoneVerified: true });
    }
  };

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center bg-white">
                <img src="/MUA_icon.svg" alt="MUA logo" className="w-10 h-10 object-contain" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900"> MUA Life Insurance</h1>
                <p className="text-sm text-gray-600">KYC Update Portal</p>
              </div>
            </div>
            <button className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">
              Need Help?
            </button>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${currentStep === step.id
                    ? 'bg-blue-600 text-white shadow-lg scale-110'
                    : currentStep > step.id
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                  {step.icon}
                </div>
                <span className={`text-xs mt-2 font-medium ${currentStep === step.id ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                  {step.name}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 rounded transition-all ${currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Step 1: Basic Details */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Update Your KYC Details</h2>
                <p className="text-gray-600">Please provide accurate information in all mandatory fields marked with *</p>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Left Column */}
                <div className="space-y-6">

                  {/* Policy Information */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Policy Information</h3>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Policy Number
                      </label>
                      <input
                        type="text"
                        name="policyNumber"
                        value={formData.policyNumber}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        placeholder="Enter your policy number"
                      />
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            First Name *
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="First name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Last Name *
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Last name"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Middle Name
                        </label>
                        <input
                          type="text"
                          name="middleName"
                          value={formData.middleName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Middle name (optional)"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date of Birth *
                          </label>
                          <input
                            type="date"
                            name="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Gender *
                          </label>
                          <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          National Identity Card (NIC) Number *
                        </label>
                        <input
                          type="text"
                          name="nic"
                          value={formData.nic}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                          placeholder="A1234567890123"
                          maxLength="14"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">

                  {/* Contact Information */}
                  <div className="border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center mr-3">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start space-x-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">
                        OTP verification required in next step
                      </p>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="your.email@example.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="+230 XXXX XXXX"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center mr-3">
                        <Home className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Residential Address</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address Line 1 *
                        </label>
                        <input
                          type="text"
                          name="addressLine1"
                          value={formData.addressLine1}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="House/Flat No., Building"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address Line 2
                        </label>
                        <input
                          type="text"
                          name="addressLine2"
                          value={formData.addressLine2}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Street, Area (optional)"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            City/Town *
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="City/Town"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            District *
                          </label>
                          <select
                            name="district"
                            value={formData.district}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select District</option>
                            <option value="Black River">Black River</option>
                            <option value="Flacq">Flacq</option>
                            <option value="Grand Port">Grand Port</option>
                            <option value="Moka">Moka</option>
                            <option value="Pamplemousses">Pamplemousses</option>
                            <option value="Plaines Wilhems">Plaines Wilhems</option>
                            <option value="Port Louis">Port Louis</option>
                            <option value="Rivière du Rempart">Rivière du Rempart</option>
                            <option value="Savanne">Savanne</option>
                            <option value="Rodrigues">Rodrigues</option>
                            <option value="Agalega Islands">Agalega Islands</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country *
                        </label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Important Note - Full Width */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Important Guidelines:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <span>• Information must match official documents</span>
                      <span>• NIC format: 1 letter followed by 13 digits</span>
                      <span>• Active phone number required for OTP</span>
                      <span>• All mandatory fields marked with *</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Contact Details</h2>
                <p className="text-gray-600">We'll send OTP to verify your email and phone number</p>
              </div>

              {/* Email Verification */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-medium text-gray-900">Email Verification</p>
                      <p className="text-sm text-gray-600">{formData.email}</p>
                    </div>
                  </div>
                  {verification.emailVerified && (
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  )}
                </div>

                {!verification.emailVerified && (
                  <div className="space-y-3">
                    {!verification.emailSent ? (
                      <button
                        onClick={() => sendOTP('email')}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Send OTP to Email
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={verification.emailOtp}
                          onChange={(e) => setVerification({ ...verification, emailOtp: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter 6-digit OTP"
                          maxLength="6"
                        />
                        <div className="flex space-x-3">
                          <button
                            onClick={() => verifyOTP('email')}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            Verify Email
                          </button>
                          <button
                            onClick={() => sendOTP('email')}
                            className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            Resend
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Phone Verification */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-medium text-gray-900">Phone Verification</p>
                      <p className="text-sm text-gray-600">{formData.phone}</p>
                    </div>
                  </div>
                  {verification.phoneVerified && (
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  )}
                </div>

                {!verification.phoneVerified && (
                  <div className="space-y-3">
                    {!verification.phoneSent ? (
                      <button
                        onClick={() => sendOTP('phone')}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Send OTP to Phone
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={verification.phoneOtp}
                          onChange={(e) => setVerification({ ...verification, phoneOtp: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter 6-digit OTP"
                          maxLength="6"
                        />
                        <div className="flex space-x-3">
                          <button
                            onClick={() => verifyOTP('phone')}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            Verify Phone
                          </button>
                          <button
                            onClick={() => sendOTP('phone')}
                            className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            Resend
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {verification.emailVerified && verification.phoneVerified && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <p className="text-green-800 font-medium">Both email and phone verified successfully!</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Document Upload */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Documents</h2>
                <p className="text-gray-600">Please upload the required documents for verification</p>
              </div>

              <div className="space-y-4">

                {/* Topic 1: Proof of Identity */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <div className="flex items-start space-x-4">
                    <CreditCard className="w-8 h-8 text-blue-600 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">Proof of Identity (Required)</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {identityType === 'nic' && 'Please upload NIC front and back'}
                        {identityType === 'passport' && 'Please upload passport address page'}
                        {!identityType && 'Select your identity document type'}
                      </p>

                      <div className="mb-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Select Identity Document Type *</div>
                        <div className="flex flex-wrap items-center gap-3">
                          <label
                            className={`
                              px-5 py-2 border rounded-[10px] cursor-pointer transition-all duration-200 
                              ${identityType === 'nic'
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                              }
                            `}
                          >
                            <input 
                              type="radio" 
                              name="identityType" 
                              className="hidden" 
                              onChange={() => setIdentityType('nic')} 
                              checked={identityType === 'nic'} 
                            />
                            🪪 NIC
                          </label>

                          <label
                            className={`
                              px-5 py-2 border rounded-[10px] cursor-pointer transition-all duration-200 
                              ${identityType === 'passport'
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                              }
                            `}
                          >
                            <input 
                              type="radio" 
                              name="identityType" 
                              className="hidden" 
                              onChange={() => setIdentityType('passport')} 
                              checked={identityType === 'passport'} 
                            />
                            📘 Passport
                          </label>
                        </div>
                      </div>

                      {identityType === 'nic' && (
                        <>
                          <label className="block text-xs text-gray-600 mb-1">NIC — Front Page*</label>
                          <input type="file" onChange={(e) => handleFileUpload('nicFront', e)} accept=".pdf,.jpg,.jpeg,.png" className="text-sm mb-2 w-full" />
                          {documents.nicFront && <p className="text-sm text-green-600 mt-1 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />{documents.nicFront.name}</p>}

                          <label className="block text-xs text-gray-600 mt-3 mb-1">NIC — Back Page*</label>
                          <input type="file" onChange={(e) => handleFileUpload('nicBack', e)} accept=".pdf,.jpg,.jpeg,.png" className="text-sm mb-2 w-full" />
                          {documents.nicBack && <p className="text-sm text-green-600 mt-1 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />{documents.nicBack.name}</p>}
                        </>
                      )}

                      {identityType === 'passport' && (
                        <>
                          <label className="block text-xs text-gray-600 mb-1">Passport Address Page *</label>
                          <input type="file" onChange={(e) => handleFileUpload('passport', e)} accept=".pdf,.jpg,.jpeg,.png" className="text-sm w-full" />
                          {documents.passport && <p className="text-sm text-green-600 mt-1 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />{documents.passport.name}</p>}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Topic 2: Proof of Address */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <div className="flex items-start space-x-4">
                    <Home className="w-8 h-8 text-blue-600 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">Proof of Address</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Upload any one of the document as a proof of address.
                      </p>

                      <div className="mb-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Select Address Proof Document *</div>
                        <div className="flex flex-wrap items-center gap-3">
                          <label
                            className={`
                              px-5 py-2 border rounded-[10px] cursor-pointer transition-all duration-200 
                              ${addressProofType === 'utility'
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                              }
                            `}
                          >
                            <input 
                              type="radio" 
                              name="addressProofType" 
                              className="hidden" 
                              onChange={() => setAddressProofType('utility')} 
                              checked={addressProofType === 'utility'} 
                            />
                            🧾 Utility Bill
                          </label>

                          <label
                            className={`
                              px-5 py-2 border rounded-[10px] cursor-pointer transition-all duration-200 
                              ${addressProofType === 'bank'
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                              }
                            `}
                          >
                            <input 
                              type="radio" 
                              name="addressProofType" 
                              className="hidden" 
                              onChange={() => setAddressProofType('bank')} 
                              checked={addressProofType === 'bank'} 
                            />
                            🏦 Bank Statement
                          </label>
                        </div>
                      </div>

                      {addressProofType === 'utility' && (
                        <>
                          <label className="block text-xs text-gray-600 mb-1">Utility Bill *</label>
                          <input type="file" onChange={(e) => handleFileUpload('utilityBill', e)} accept=".pdf,.jpg,.jpeg,.png" className="text-sm w-full mb-4" />
                          {documents.utilityBill && <p className="text-sm text-green-600 mt-1 mb-4 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />{documents.utilityBill.name}</p>}
                          
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="mb-3">
                              <div className="text-sm font-medium text-gray-700 mb-2">Is the utility bill in your name?</div>
                              <p className="text-sm text-gray-600 mb-2">{billOwner === false && " If utility bill is not in your name, provide a proof of relationship."}</p>
                              <div className="flex flex-wrap items-center gap-3">
                                <label
                                  className={`
                                    px-5 py-2 border rounded-[10px] cursor-pointer transition-all duration-200 
                                    ${billOwner === true
                                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                    }
                                  `}
                                >
                                  <input type="radio" name="billOwner" className="hidden" onChange={() => setBillOwner(true)} checked={billOwner === true} />
                                  ✅ Yes
                                </label>

                                <label
                                  className={`
                                    px-5 py-2 border rounded-[10px] cursor-pointer transition-all duration-200 
                                    ${billOwner === false
                                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                    }
                                  `}
                                >
                                  <input type="radio" name="billOwner" className="hidden" onChange={() => setBillOwner(false)} checked={billOwner === false} />
                                  ❌ No
                                </label>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {addressProofType === 'bank' && (
                        <>
                          <label className="block text-xs text-gray-600 mb-1">Bank Statement *</label>
                          <input type="file" onChange={(e) => handleFileUpload('bankStatement', e)} accept=".pdf,.jpg,.jpeg,.png" className="text-sm w-full" />
                          {documents.bankStatement && <p className="text-sm text-green-600 mt-1 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />{documents.bankStatement.name}</p>}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Topic 3: Proof of Relationship — only shown when utility bill is selected AND NOT in client's name */}
                {addressProofType === 'utility' && billOwner === false && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors">
                    <div className="flex items-start space-x-4">
                      <User className="w-8 h-8 text-blue-600 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">Proof of Relationship (Required)</h3>
                        <p className="text-sm text-gray-600 mb-3">Please upload either birth certificate, marriage certificate, or lease documents.</p>

                        <label className="block text-xs text-gray-600 mb-1">Relationship Document *</label>
                        <input type="file" onChange={(e) => handleFileUpload('relationshipDoc', e)} accept=".pdf,.jpg,.jpeg,.png" className="text-sm w-full" />
                        {documents.relationshipDoc && <p className="text-sm text-green-600 mt-1 flex items-center"><CheckCircle className="w-4 h-4 mr-1" />{documents.relationshipDoc.name}</p>}
                      </div>
                    </div>
                  </div>
                )}

              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Important:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>File size should not exceed 5MB</li>
                      <li>Accepted formats: PDF, JPG, JPEG, PNG</li>
                      <li>Documents should be clear and readable</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Information</h2>
                <p className="text-gray-600">Please verify all details before submission</p>
              </div>

              {/* Personal Details */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-blue-600" />
                  Personal Details
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Policy Number</p>
                    <p className="font-medium text-gray-900">{formData.policyNumber}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Full Name</p>
                    <p className="font-medium text-gray-900">
                      {formData.firstName} {formData.middleName} {formData.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Date of Birth</p>
                    <p className="font-medium text-gray-900">{formData.dateOfBirth}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Gender</p>
                    <p className="font-medium text-gray-900 capitalize">{formData.gender}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">NIC Number</p>
                    <p className="font-medium text-gray-900">{formData.nic}</p>
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-blue-600" />
                  Contact Details
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Email</p>
                    <p className="font-medium text-gray-900 flex items-center">
                      {formData.email}
                      <CheckCircle className="w-4 h-4 ml-2 text-green-500" />
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Phone</p>
                    <p className="font-medium text-gray-900 flex items-center">
                      {formData.phone}
                      <CheckCircle className="w-4 h-4 ml-2 text-green-500" />
                    </p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Home className="w-5 h-5 mr-2 text-blue-600" />
                  Address
                </h3>
                <p className="text-sm text-gray-900">
                  {formData.addressLine1}, {formData.addressLine2 && `${formData.addressLine2}, `}
                  {formData.city}, {formData.district}, {formData.country}
                </p>
              </div>

              {/* Documents */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-blue-600" />
                  Uploaded Documents
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center text-green-600">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Proof of Identity: {documents.identityProof?.name || 'Not uploaded'}
                  </p>
                  <p className="flex items-center text-green-600">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Proof of Address: {documents.addressProof?.name || 'Not uploaded'}
                  </p>
                  <p className="flex items-center text-green-600">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Proof of Relationship: {documents.photo?.name || 'Not uploaded'}
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Declaration:</p>
                    <p>I hereby declare that the information provided above is true and correct to the best of my knowledge. I understand that any false information may lead to rejection of my application.</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="terms"
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="terms" className="text-sm text-gray-700">
                  I accept the terms and conditions and privacy policy
                </label>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            {currentStep > 1 && (
              <button
                onClick={prevStep}
                className="flex items-center px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </button>
            )}

            {currentStep < 4 ? (
              <button
                onClick={nextStep}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ml-auto"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </button>
            ) : (
              <button
                className="flex items-center px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors ml-auto font-semibold shadow-lg"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Submit KYC Update
              </button>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-white rounded-xl shadow-md p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Need Assistance?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start space-x-3">
              <Phone className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Call Us</p>
                <p className="text-gray-600">+230 XXX XXXX</p>
                <p className="text-gray-500 text-xs">Mon-Fri, 9 AM - 5 PM</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Mail className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Email Us</p>
                <p className="text-gray-600">kyc@mua.mu</p>
                <p className="text-gray-500 text-xs">Response in 24 hours</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Help Center</p>
                <a href="#" className="text-blue-600 hover:underline">View FAQs</a>
                <p className="text-gray-500 text-xs">Quick answers</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm">
            <p className="mb-2">© 2025 SecureLife Insurance Mauritius. All rights reserved.</p>
            <div className="space-x-4">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <span>•</span>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <span>•</span>
              <a href="#" className="hover:text-white transition-colors">FSC Regulated</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}